import { Navigate } from 'react-router-dom'
import { useModuleSwitches } from '../contexts/ModuleSwitchContext.jsx'
import { HELD_MODULES, STANDARD_MODULES, isModuleReleased } from '../shared/constants/modules.js'

/**
 * A held module's ADDRESS obeys the same server switch as its tile and its nav tab.
 *
 * WHY THIS EXISTS. Hiding the tile and the tab hides the menu, not the module: `/capex` typed into the
 * address bar, a bookmark, or Bid Workup's own redirect of a capital bid (and an old
 * `/capital/estimates/:id` link, which App.jsx sends on outside Capital's switch) to `/capex/estimates/:id` would still mount a module whose service refuses every call (an
 * estimate's address now says so instead of bouncing: `whenOff` below). Update 1 did this for Capital with a
 * per-tenant flag (release/2.98.0 FeatureGatedRoute); update 2 holds Capital, Capex and Invoicing on
 * the SERVER'S switch (contexts/ModuleSwitchContext.jsx), which is the one production leaves unset.
 *
 * FAILS CLOSED, and never flashes. Switched on: the module renders. Still loading: `fallback` renders
 * and nothing is decided — redirecting early would throw staging out of a module it has, and rendering
 * early would mount one production refuses. Anything else: the portal, or `whenOff` / `whenUnknown`
 * when the route gives them (below). Admins included.
 *
 * `whenOff` is for an address that must SAY why it did not open rather than bounce: a link to a capital
 * estimate with Project Financials off used to land on the portal with nothing said (review finding 17;
 * modules/capex/CapexSwitchedOff.jsx). It is drawn ONLY FOR A REAL "OFF": once the server has answered
 * for this company (`answered`, contexts/ModuleSwitchContext.jsx) and said the module is not on. A
 * lookup that failed — identity restarting mid-deploy, a timeout, a body that is not JSON — holds the
 * module just the same, but nobody has said it is off, and a page saying "switched off for this
 * company" sent an administrator to find it ON. Then `whenUnknown` is drawn instead (the same page
 * saying it could not check, with a reload). Neither ever mounts the module. A route that gives
 * neither goes to the portal whatever the reason, as before.
 *
 * A module that is not held passes straight through, so wrapping a route changes nothing until
 * HELD_MODULES names it.
 *
 * A STANDARD module (STANDARD_MODULES: Timekeeping, Projects, ... the twelve every company has) is the
 * other way round: it renders at once - loading, a failed lookup, no answer, all ON - and only the
 * server's answer that it is switched off for this company sends the address to the portal (or
 * `whenOff`). So no ordinary page waits on the lookup, and nothing disappears by itself.
 */
export default function ModuleSwitchRoute({ moduleId, fallback = null, whenOff = null, whenUnknown = null, children }) {
  const { switches, loading, answered } = useModuleSwitches()
  if (!HELD_MODULES.includes(moduleId) && !STANDARD_MODULES.includes(moduleId)) return children
  if (isModuleReleased(moduleId, switches)) return children
  if (loading) return fallback
  if (answered === true) return whenOff ?? <Navigate to="/portal" replace />
  return whenUnknown ?? <Navigate to="/portal" replace />
}
